import { useCallback, useEffect, useState } from 'react';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import type { ExperienceVersion, UseExperienceVersionReturn } from './ExperienceSwitch.types';
import type { ExperienceId } from './experiences.constants';
import { getExperienceDefinition } from './experiences.constants';
import { getExperienceStore } from './experienceStore';

/**
 * Headless version state for an experience. Safe to call from *any* surface — a nav pill, a
 * dashboard row, an unrelated page — because it fires no telemetry. The page that owns the
 * experience should call `useExperienceSwitch` instead, exactly once.
 *
 * Two inputs combine: the experience's kill-switch flag and a per-admin localStorage preference.
 * When the kill switch is off, `storedVersion` is always 'classic' and `isNextAvailable` is false,
 * regardless of any stored preference.
 */
export const useExperienceVersion = (id: ExperienceId): UseExperienceVersionReturn => {
  const definition = getExperienceDefinition(id);
  const store = getExperienceStore(id);

  const [isNextAvailable] = useIsFeatureEnabled(definition.flagKey);

  // Local mirrors of the shared store. Subscribing on mount means a change on one surface (e.g.
  // viewing the new build) immediately re-renders the others without a remount.
  const [persistedVersion, setPersistedVersion] = useState<ExperienceVersion>(store.getVersion);
  const [hasSeenNext, setHasSeenNext] = useState<boolean>(store.getHasSeenNext);

  useEffect(() => {
    // Re-sync once on mount in case the store changed between the initial render and this effect.
    setPersistedVersion(store.getVersion());
    setHasSeenNext(store.getHasSeenNext());
    return store.subscribe(() => {
      setPersistedVersion(store.getVersion());
      setHasSeenNext(store.getHasSeenNext());
    });
  }, [store]);

  const setVersion = useCallback(
    (version: ExperienceVersion) => {
      store.setVersion(version);
    },
    [store],
  );

  const markNextSeen = useCallback(() => {
    store.markNextSeen();
  }, [store]);

  const storedVersion: ExperienceVersion = isNextAvailable ? persistedVersion : 'classic';
  const showNewPill = isNextAvailable && !hasSeenNext;

  return { storedVersion, isNextAvailable, showNewPill, setVersion, markNextSeen };
};
